import React, { useEffect, useState, useMemo, Fragment } from "react";
import {
  Button,
  Drawer,
  Modal,
  Notification,
  Table,
  MediaQuery,
  Popover,
  ActionIcon,
} from "@mantine/core";

import { db } from "../../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { TripConfig } from "../../components/listings/configurations/trip-config";
import TripTimeline from "./trip-drawer-details";
import { ResponsiveDataTable } from "../../components/listings/responsive-data-table";
import { Plus, Edit, Copy, Dots } from "tabler-icons-react";
import { TripEditor } from "../../components/shared/trip-editor";
import { Trip, VehicleGroup, TripStatus } from "@captiva/shared-types";

export interface AuthUser {
  uid?: string;
  email?: string;
  claims?: {
    companyId?: string;
    email?: string;
    user_id?: string;
  };
}

interface RowMenuButtonProps {
  element: Trip;
  onEdit: (element: Trip) => void;
  onDuplicate: (element: Trip) => void;
}

const RowMenuButton = ({ element, onEdit, onDuplicate }: RowMenuButtonProps) => {
  const [opened, setOpened] = useState(false);
  return (
    <Popover
      opened={opened}
      position="left"
      onClose={() => setOpened(false)}
      target={
        <ActionIcon
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            setOpened((o) => !o);
          }}
        >
          <Dots />
        </ActionIcon>
      }
      width={260}
      withArrow
    >
      <div style={{ display: "flex", flexDirection: "column", backgroundColor: "white" }}>
        <Button
          variant="outline"
          leftIcon={<Edit size={16} />}
          color="cap-navy"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(element);
            setOpened(false);
          }}
          mb="md"
        >
          Edit
        </Button>
        <Button
          variant="outline"
          leftIcon={<Copy />}
          color="cap-navy"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(element);
            setOpened(false);
          }}
        >
          Duplicate
        </Button>
      </div>
    </Popover>
  );
};

interface VehicleOption {
  id: string;
  name: string;
  registrationNumber?: string;
  description?: string;
  measurementUnit?: string;
  group?: string;
}

interface FleetOperatorOption {
  id: string;
  fullName: string;
  employeeCode?: string;
  identification?: string;
}

interface TripTableProps {
  elements: Trip[];
  auth: AuthUser | null;
  reloadCallback: (trip?: Trip) => void;
  companyId: string;
  selectedTripId: string | null;
  tripMode?: string | null;
  onOpenTrip?: (id: string, action: string, options?: { replace?: boolean }) => void;
  onCloseTrip?: (options?: { replace?: boolean }) => void;
}

export function TripTable({
  elements,
  auth,
  reloadCallback,
  companyId,
  selectedTripId,
  tripMode,
  onOpenTrip,
  onCloseTrip,
}: TripTableProps) {
  const newTrip: Trip = {
    id: null,
    description: "",
    status: TripStatus.PLANNED,
    deliveryAddress: "",
    pickupAddress: "",
    fleetUseFleetForRebateClaim: false,
    pickupDate: new Date().toISOString(),
    deliveryDate: new Date().toISOString(),
    notificationEmailAddresses: [],
    notificationWhatsappNumbers: [],
    externalReference: "",
    requiresPreInspection: false,
    fleetId: "",
    fleetGroupId: "",
    fleetGroupName: "",
    fleetDescription: "",
    fleetRegistration: "",
    fleetDisplayName: "",
    fleetOdoOrHourBased: "",
    fleetOperatorId: "",
    fleetOperatorNameAndSurname: "",
    fleetOperatorEmployeeCode: "",
    fleetOperatorIdentification: "",
    companyId: companyId,
    isTrackingActive: false,
    notes: "",
    creatorEmail: auth?.claims?.email || "",
    creatorUid: auth?.claims?.user_id || "",
    locationSnapshots: [],
    capturedTripArrive: [],
    capturedTripPreInspection: [],
    capturedTripDepart: [],
    capturedTripCheckPoint: [],
    capturedTripComplete: [],
    capturedTripDelay: [],
    requiresPreInspectionFlag: false,
    tripToken: "",
  } as unknown as Trip;

  const [editedElement, setEditedElement] = useState<Trip | undefined>(undefined);
  const [isFetchedDependencies, setIsFetchedDependencies] = useState(false);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [fleetOperators, setfleetOperators] = useState<FleetOperatorOption[]>([]);
  const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([]);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [messageColor, setMessageColor] = useState("green");
  const [tripDrawerOpen, setTripdrawerOpen] = useState(false);
  const [viewedTrip, setViewedTrip] = useState<Trip | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleEdit = () => {
    setIsFetchedDependencies(false);
  };

  const handleDelete = async (deleteCandidate: Trip) => {
    if (!deleteCandidate.id) {
      setMessageColor("green");
      setMessage("Successfully deleted");
      return;
    }
    closeEdit();
    reloadCallback();
  };

  const handleSave = async (updatedTrip: Partial<Trip>) => {
    setLoading(true);
    const vehicle = vehicles.find((v) => v.id === updatedTrip.fleetId);
    const fleetOperator = fleetOperators.find(
      (d) => d.id === updatedTrip.fleetOperatorId
    );
    let group: VehicleGroup | undefined;
    if (vehicle && vehicle.group) {
      group = vehicleGroups.find((g) => g.id === vehicle.group);
    }
    
    let nextStatus = editedElement?.status || TripStatus.PLANNED;
    if (editedElement?.status === TripStatus.PLANNED && updatedTrip.fleetOperatorId) {
      nextStatus = TripStatus.ALLOCATED;
    }
    
    let pickupIso = updatedTrip.pickupDate;
    if (updatedTrip.pickupDate) {
      const d = new Date(updatedTrip.pickupDate);
      if (!isNaN(d.getTime())) {
        pickupIso = d.toISOString();
      } else {
        pickupIso = new Date().toISOString();
      }
    } else {
      pickupIso = new Date().toISOString();
    }
    let deliveryIso = updatedTrip.deliveryDate;
    if (updatedTrip.deliveryDate) {
      const d = new Date(updatedTrip.deliveryDate);
      if (!isNaN(d.getTime())) {
        deliveryIso = d.toISOString();
      } else {
        deliveryIso = new Date().toISOString();
      }
    } else {
      deliveryIso = new Date().toISOString();
    }

    const tripToSave: any = {
      ...editedElement,
      ...updatedTrip,
      status: nextStatus,
      pickupDate: pickupIso,
      deliveryDate: deliveryIso,
      fleetGroupId: group?.id || "none",
      fleetGroupName: group?.name || "none",
      fleetId: vehicle?.id || "unassigned",
      fleetDisplayName: vehicle?.name || "unassigned",
      fleetDescription: vehicle?.description || "unassigned",
      fleetRegistration: vehicle?.registrationNumber || "unassigned",
      fleetOdoOrHourBased: vehicle?.measurementUnit || "unassigned",
      fleetOperatorNameAndSurname: fleetOperator?.fullName || "unassigned",
      fleetOperatorId: fleetOperator?.id || "unassigned",
      fleetOperatorEmployeeCode: fleetOperator?.employeeCode || "unassigned",
      fleetOperatorIdentification: fleetOperator?.identification || "unassigned",
    };

    if (tripToSave.pickupDate) {
      tripToSave.pickupTimestamp = Timestamp.fromDate(new Date(tripToSave.pickupDate));
    }
    if (tripToSave.deliveryDate) {
      tripToSave.deliveryTimestamp = Timestamp.fromDate(new Date(tripToSave.deliveryDate));
    }

    try {
      if (tripToSave.id) {
        const docRef = doc(db, "trips", tripToSave.id);
        await updateDoc(docRef, tripToSave);
        setMessageColor("green");
        setMessage("Successfully updated!");
        reloadCallback(tripToSave as Trip);
        closeEdit();
      } else {
        const docRef = await addDoc(collection(db, "trips"), tripToSave);
        await updateDoc(docRef, { id: docRef.id });
        setMessageColor("green");
        setMessage("Successfully created!");
        reloadCallback({ ...tripToSave, id: docRef.id } as Trip);
        setEditedElement(undefined);
      }
    } catch (error: any) {
      console.error(error);
      setMessageColor("red");
      setMessage("Save unsuccessful: " + error.toString());
    }
    setLoading(false);
  };

  function getDuplicatedTripObject(currentTrip: Trip): Trip {
    return {
      ...currentTrip,
      id: null,
      status: TripStatus.PLANNED,
      locationSnapshots: [],
      capturedTripPreInspection: [],
      capturedTripDepart: [],
      capturedTripCheckPoint: [],
      capturedTripDelay: [],
      capturedTripArrive: [],
      capturedTripComplete: [],
      capturedRefuel: [],
      capturedFuelTankIssue: [],
      isTrackingActive: false,
      tripToken: "",
      description: `${currentTrip.description} (copy)`,
      pickupDate: new Date().toISOString(),
      deliveryDate: new Date().toISOString(),
      tripOwnerUid: "",
      creatorEmail: "",
      isOutOfSync: false
    } as unknown as Trip;
  }

  const handleDuplicate = (element: Trip) => {
    const duplicatedTrip = getDuplicatedTripObject(element);
    setEditedElement(duplicatedTrip);
    handleEdit();
  };

  useEffect(() => {
    async function fetchDeps() {
      if (!isFetchedDependencies && !!companyId) {
        setIsFetchedDependencies(true);
        const fleetSnapshots = await getDocs(
          query(collection(db, "fleet"), where("companyId", "==", companyId))
        );
        if (fleetSnapshots.size > 0) {
          const entries: VehicleOption[] = [];
          fleetSnapshots.forEach((doc) =>
            entries.push({ id: doc.id, ...doc.data() } as VehicleOption)
          );
          entries.unshift({ id: "", name: "No assignment" });
          setVehicles(entries);
        }

        const fleetOperatorSnapshots = await getDocs(
          query(
            collection(db, "fleetOperators"),
            where("companyId", "==", companyId)
          )
        );
        if (fleetOperatorSnapshots.size > 0) {
          const fleetOperatorEntries: FleetOperatorOption[] = [];
          fleetOperatorSnapshots.forEach((doc) =>
            fleetOperatorEntries.push({ id: doc.id, ...doc.data() } as FleetOperatorOption)
          );
          fleetOperatorEntries.unshift({ id: "", fullName: "No assignment" });
          setfleetOperators(fleetOperatorEntries);
        }

        const fleetGroupSnapshots = await getDocs(
          query(
            collection(db, "fleetGroups"),
            where("companyId", "==", companyId)
          )
        );
        if (fleetGroupSnapshots.size > 0) {
          const groupEntries: VehicleGroup[] = [];
          fleetGroupSnapshots.forEach((doc) =>
            groupEntries.push({ id: doc.id, ...doc.data() } as VehicleGroup)
          );
          setVehicleGroups(groupEntries);
        }
      }
    }
    fetchDeps();
  }, [isFetchedDependencies, companyId]);

  useEffect(() => {
    if (!selectedTripId) {
      setViewedTrip(undefined);
      setTripdrawerOpen(false);
      return;
    }

    const loadedTrip = elements.find((element) => element.id === selectedTripId);
    if (loadedTrip) {
      setViewedTrip(loadedTrip);
      setTripdrawerOpen(true);
      return;
    }

    let cancelled = false;

    const loadTrip = async () => {
      try {
        const snapshot = await getDoc(doc(db, "trips", selectedTripId));
        if (!snapshot.exists() || cancelled) {
          if (!cancelled) {
            onCloseTrip?.({ replace: true });
          }
          return;
        }

        const loadedEntry = { id: snapshot.id, ...snapshot.data() } as unknown as Trip;
        setViewedTrip(loadedEntry);
        setTripdrawerOpen(true);
      } catch (error) {
        console.error("Failed to load trip", error);
        if (!cancelled) {
          onCloseTrip?.({ replace: true });
        }
      }
    };

    loadTrip();

    return () => {
      cancelled = true;
    };
  }, [elements, onCloseTrip, selectedTripId]);

  const viewElement = (element: Trip) => {
    if (element.id) {
      onOpenTrip?.(element.id, "view", { replace: false });
    }
  };

  const closeEdit = () => setEditedElement(undefined);

  const columns = [
    { key: "description", label: "Description", render: (element: Trip) => element.description },
    { key: "status", label: "Status", render: (element: Trip) => element.status, hideOnMobile: true },
    { key: "pickupAddress", label: "Pickup address", render: (element: Trip) => element.pickupAddress },
    { key: "deliveryAddress", label: "Delivery address", render: (element: Trip) => element.deliveryAddress, hideOnMobile: true },
    { key: "pickupDate", label: "Pickup date", render: (element: Trip) => new Date(element.pickupDate).toDateString(), hideOnMobile: true },
    { key: "fleetOperatorNameAndSurname", label: "Driver", render: (element: Trip) => element.fleetOperatorNameAndSurname, hideOnMobile: true },
    { key: "fleetDisplayName", label: "Vehicle", render: (element: Trip) => element.fleetDisplayName, hideOnMobile: true },
    {
      key: "tracking",
      label: "Tracking",
      render: (element: Trip) => element.tripToken ? (
        <a
          href={`https://captivatrip.web.app/?tripToken=${element.tripToken}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Tracking page
        </a>
      ) : (
        "Not started"
      ),
      hideOnMobile: true
    }
  ];

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (colKey: string) => {
    if (sortColumn === colKey) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      setSortColumn(colKey);
      setSortDirection("asc");
    }
  };

  const sortedElements = useMemo(() => {
    if (!sortColumn) return elements;

    const sorted = [...elements];
    const modifier = sortDirection === "asc" ? 1 : -1;

    sorted.sort((a: any, b: any) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];

      const isNullA = valA === undefined || valA === null || valA === "";
      const isNullB = valB === undefined || valB === null || valB === "";
      if (isNullA && isNullB) return 0;
      if (isNullA) return 1;
      if (isNullB) return -1;

      if (sortColumn === "pickupDate" || sortColumn === "deliveryDate") {
        const getDateMs = (ts: any) => {
          if (!ts) return 0;
          if (typeof ts.toMillis === "function") return ts.toMillis();
          if (typeof ts.toDate === "function") return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          if (typeof ts === "number") return ts;
          return new Date(ts).getTime() || 0;
        };
        return (getDateMs(valA) - getDateMs(valB)) * modifier;
      }

      const numA = Number(valA);
      const numB = Number(valB);
      if (!isNaN(numA) && !isNaN(numB) && typeof valA !== "boolean" && typeof valB !== "boolean") {
        return (numA - numB) * modifier;
      }

      const strA = String(valA);
      const strB = String(valB);
      return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" }) * modifier;
    });

    return sorted;
  }, [elements, sortColumn, sortDirection]);

  return (
    <>
      <ResponsiveDataTable
        elements={sortedElements}
        columns={columns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        rowProps={(element) => ({
          onClick: () => viewElement(element),
          style: { cursor: "pointer" },
          className: "row"
        })}
        actionsRender={(element) => (
          <RowMenuButton
            element={element}
            onEdit={(elem) => {
              setEditedElement({ ...newTrip, ...elem });
              handleEdit();
            }}
            onDuplicate={handleDuplicate}
          />
        )}
      />
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Trip" size="lg">
          <TripEditor
            trip={editedElement}
            tripConfig={TripConfig}
            collection="trips"
            showLoadingIndicator={loading}
            dependencyList={{
              fleetId: [...vehicles],
              fleetOperatorId: [...fleetOperators],
              fleetGroupId: [...vehicleGroups],
            }}
            dependencyLabel={{ fleetId: "name", fleetOperatorId: "fullName" }}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Modal>
      )}
      <Drawer
        size="85%"
        position="right"
        opened={tripDrawerOpen}
        lockScroll={false}
        onClose={() => {
          onCloseTrip?.({ replace: false });
        }}
      >
        {viewedTrip && <TripTimeline trip={viewedTrip} key={viewedTrip.id} />}
      </Drawer>
      <Button
        onClick={() => setEditedElement(newTrip)}
        fullWidth
        my="xl"
        color="cap-navy"
        variant="outline"
        leftIcon={<Plus />}
      >
        Create new
      </Button>
      {message && (
        <Notification color={messageColor} onClose={() => setMessage("")}>
          {message}
        </Notification>
      )}
    </>
  );
}
